// Places API (New) nearby search + Routes API walking distances.
//
// Why not the legacy PlacesService.nearbySearch / DistanceMatrixService:
// - legacy nearbySearch silently ignores Table A types it does not know
//   (playground, bus_stop, preschool, ...) and returns generic results;
// - the legacy Distance Matrix API is not enabled on the project key
//   (REQUEST_DENIED), while Routes API computeRouteMatrix is.
import { GOOGLE_MAPS_API_KEY } from '../constants/placeTypes';
import { calculateDistance } from '../utils/placeSearchTypes';

const toLatLngLiteral = (loc) => ({
  lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
  lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng
});

// Search up to 20 places of any of `types` within `radius` m (crow-flies),
// nearest first. Returns objects shaped like the legacy results the map
// already renders: place_id, name, types, vicinity, geometry.location.
export const searchNearbyByTypes = async (center, types, radius) => {
  const { Place, SearchNearbyRankPreference } = window.google.maps.places;
  const { places } = await Place.searchNearby({
    fields: ['id', 'displayName', 'location', 'types', 'formattedAddress'],
    locationRestriction: { center, radius },
    includedTypes: types,
    maxResultCount: 20,
    rankPreference: SearchNearbyRankPreference.DISTANCE,
    language: 'sv',
    region: 'se'
  });
  return (places || [])
    .filter((p) => p.location)
    .map((p) => {
      const location = toLatLngLiteral(p.location);
      return {
        place_id: p.id,
        name: p.displayName || 'Okänd plats',
        types: p.types || [],
        vicinity: p.formattedAddress || '',
        geometry: { location },
        straightLineMeters: calculateDistance(center.lat, center.lng, location.lat, location.lng)
      };
    });
};

// Walking distance (m) and duration (min) from origin to each place, via
// Routes API computeRouteMatrix in batches. Returns Map place_id -> result.
// Elements without a route fall back to crow-flies × 1.3 at 83 m/min.
export const walkingDistancesTo = async (origin, places) => {
  const out = new Map();
  const fallback = (place) => {
    const meters = Math.round(place.straightLineMeters * 1.3);
    return { walkingDistanceMeters: meters, walkingDurationMinutes: Math.round(meters / 83), estimated: true };
  };

  const BATCH = 100;
  for (let i = 0; i < places.length; i += BATCH) {
    const batch = places.slice(i, i + BATCH);
    let rows = null;
    try {
      const res = await fetch('https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': 'originIndex,destinationIndex,duration,distanceMeters,condition'
        },
        body: JSON.stringify({
          origins: [{ waypoint: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } } }],
          destinations: batch.map((p) => ({
            waypoint: { location: { latLng: { latitude: p.geometry.location.lat, longitude: p.geometry.location.lng } } }
          })),
          travelMode: 'WALK',
          languageCode: 'sv'
        })
      });
      if (res.ok) rows = await res.json();
      else console.error('computeRouteMatrix failed', res.status, await res.text());
    } catch (e) {
      console.error('computeRouteMatrix error', e);
    }

    batch.forEach((p) => out.set(p.place_id, fallback(p)));
    if (Array.isArray(rows)) {
      rows.forEach((row) => {
        const p = batch[row.destinationIndex];
        if (!p || row.condition !== 'ROUTE_EXISTS' || row.distanceMeters === undefined) return;
        const seconds = parseInt(String(row.duration || '0').replace('s', ''), 10) || 0;
        out.set(p.place_id, {
          walkingDistanceMeters: row.distanceMeters,
          walkingDurationMinutes: Math.max(1, Math.round(seconds / 60)),
          estimated: false
        });
      });
    }
  }
  return out;
};
